import { Button } from "antd";
import { TableWithPagination } from "Components/TableWithPagination";
import sortIcon from "assets/svg/SortAscending.svg";
import styles from "./index.module.css";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Pagination } from "pages/Main/Components/AdminPanel/Options";
import {
  ENTITY_PER_PAGE,
  PROP_NAME_CITY,
  PROP_NAME_FROM_DATE,
  PROP_NAME_INPUT_TEXT,
  PROP_NAME_INSURANCE,
  PROP_NAME_MAIN_TAB,
  PROP_NAME_SKIP_ADS,
  PROP_NAME_SORTBY,
  PROP_NAME_TO_DATE,
} from "helpers/constants";
import { CopyButton } from "Components/CopyButton";
import { PhotoNameTab } from "Components/PhotoNameTab";
import { getDebates, InsuranceType, SortByDisputes } from "helpers/backend_helper";
import { Link, useSearchParams } from "react-router-dom";
import { useDebounce, useRequest } from "ahooks";
import { AdminContext } from "context/adminContext";
import { NotificationContext } from "context/notificationContext";
import { STATUSES_ADMIN } from "../NewDebates";
import { formatDateRange, formatDaysCount, getDaysCount, onSort } from "helpers/helper";

export const RequestDisputes = () => {
  const [skipAds, setSkipAds] = useState(0);
  const { localCountry } = useContext(AdminContext);
  const { openNotificationWithIcon } = useContext(NotificationContext);
  const [searchParams, setSearchParams] = useSearchParams();

  const debouncedSearchParams = useDebounce(searchParams, { wait: 100 });
  const [pagination, setPagination] = useState<Pagination>({ skip: 0, take: ENTITY_PER_PAGE, total: 0 });

  const searchText = searchParams.get(PROP_NAME_INPUT_TEXT); // debounced
  const citiesFilter = searchParams.get(PROP_NAME_CITY);
  const choosenTab = searchParams.get(PROP_NAME_MAIN_TAB);
  const searchToDate = searchParams.get(PROP_NAME_TO_DATE);
  const searchFromDate = searchParams.get(PROP_NAME_FROM_DATE);
  const searchInsurance = searchParams.get(PROP_NAME_INSURANCE);
  const sortByFilter = searchParams.get(PROP_NAME_SORTBY);
  const skipAdsFilter = searchParams.get(PROP_NAME_SKIP_ADS);

  const sortBy = (sortByFilter as SortByDisputes) || "";

  useEffect(() => {
    if (skipAdsFilter) {
      setSkipAds(+skipAdsFilter);
    } else {
      setSkipAds(0);
    }
  }, [skipAdsFilter]);

  const { data: debates, loading: isDebateTableLoading } = useRequest(
    () =>
      getDebates(
        {
          debateStatus: STATUSES_ADMIN[choosenTab || "0"],
          pagination: {
            skip: skipAds,
            take: ENTITY_PER_PAGE,
            total: pagination.total,
          },
          searchText,
          cityId: citiesFilter?.length ? citiesFilter.split(",") : [],
          dateFrom: STATUSES_ADMIN[choosenTab || "0"] === "COMPLETED" ? searchFromDate : null,
          dateTo: STATUSES_ADMIN[choosenTab || "0"] === "COMPLETED" ? searchToDate : null,
          insuranceType: searchInsurance?.length ? (searchInsurance.split(",") as InsuranceType[]) : [],
          sort: { sortBy },
        },
        localCountry.iso.toUpperCase(),
      ),
    {
      ready: !!localCountry?.iso,
      refreshDeps: [localCountry, debouncedSearchParams, sortBy],
      onError: (error: any) => {
        openNotificationWithIcon("error", error.response?.data?.message || error.message);
      },
      onSuccess: (data) => {
        setPagination((prev) => ({ ...prev, total: +data.totalCount }));
      },
    },
  );

  const onSortBy = useCallback(
    (sortName) => {
      onSort(sortName, searchParams, sortBy, setSearchParams);
    },
    [searchParams, setSearchParams, sortBy],
  );

  const columns = useMemo(() => {
    let columns = [
      {
        title: "ID",
        dataIndex: "id",
        key: "id",
      },
      {
        title: (
          <div className={styles.columnSort}>
            <div>Date</div>
            <Button
              type="text"
              icon={
                <img
                  src={sortIcon}
                  alt="usagePercentage"
                  style={{ width: 16, height: 16, transform: sortBy === "created_at" ? "rotate(180deg)" : null }}
                />
              }
              onClick={() => {
                onSortBy("created_at");
              }}
            />
          </div>
        ),
        dataIndex: "date",
        key: "date",
      },
      {
        title: "Item",
        dataIndex: "item",
        key: "item",
      },
      {
        title: (
          <div className={styles.columnSort}>
            <div>Price</div>
            <Button
              type="text"
              icon={
                <img
                  src={sortIcon}
                  alt="usagePercentage"
                  style={{ width: 16, height: 16, transform: sortBy === "amount" ? "rotate(180deg)" : null }}
                />
              }
              onClick={() => {
                onSortBy("amount");
              }}
            />
          </div>
        ),
        dataIndex: "price",
        key: "price",
      },
      {
        title: "Deal ID",
        dataIndex: "dealId",
        key: "dealId",
      },
      {
        title: "Insurance",
        dataIndex: "insurance",
        key: "insurance",
      },
      {
        title: (
          <div className={styles.columnSort}>
            <div>Period</div>
            <Button
              type="text"
              icon={
                <img
                  src={sortIcon}
                  alt="usagePercentage"
                  style={{ width: 16, height: 16, transform: sortBy === "period" ? "rotate(180deg)" : null }}
                />
              }
              onClick={() => {
                onSortBy("period");
              }}
            />
          </div>
        ),
        dataIndex: "period",
        key: "period",
      },
      {
        title: " ",
        dataIndex: "actions",
        key: "actions",
      },
    ];

    if (STATUSES_ADMIN[choosenTab || "0"] === "COMPLETED") {
      columns = [
        ...columns.slice(0, 7),
        {
          title: (
            <div className={styles.columnSort}>
              <div>Paid Out</div>
              <Button
                type="text"
                icon={
                  <img
                    src={sortIcon}
                    alt="usagePercentage"
                    style={{ width: 16, height: 16, transform: sortBy === "paid_out" ? "rotate(180deg)" : null }}
                  />
                }
                onClick={() => {
                  onSortBy("paid_out");
                }}
              />
            </div>
          ),
          dataIndex: "paid_out",
          key: "paid_out",
        },
        ...columns.slice(7),
      ];
    }

    return columns;
  }, [sortBy]);

  const source = useMemo(
    () =>
      debates?.debates.map((debate) => ({
        key: debate.id,
        id: (
          <CopyButton
            copyText="copied"
            sliceOfText={4}
            text={debate.id}
            wait={1000}
            key={debate.id}
            className={styles.copybtn}
          />
        ),
        date: new Date(debate.createdAt).toLocaleString("eng", {
          day: "2-digit",
          month: "short",
          hour: "2-digit",
          minute: "2-digit",
        }),
        item: <PhotoNameTab photoFullPath={debate?.product.photos?.[0]?.preview} title={debate.product.name} />,
        price: "€" + debate?.product?.price,
        dealId: (
          <CopyButton copyText="copied!" text={debate.rentId} wait={1000} sliceOfText={5} className={styles.copybtn} />
        ),
        insurance: debate.insuranceType,
        period: (
          <div>
            <div>
              {formatDateRange(
                new Date(debate.rentDateStart),
                new Date(new Date(debate.rentDateEnd).setUTCHours(0, 0, 0, 0)),
              ) + ","}
            </div>
            <div>
              {formatDaysCount(
                getDaysCount(debate.rentDateStart, new Date(debate.rentDateEnd).setUTCHours(0, 0, 0, 0)),
              )}
            </div>
          </div>
        ),
        paid_out: "€" + debate?.paidOut, //paidOUT нету в эндпойнте
        actions: (
          <div className={styles.buttonsWrapper}>
            {STATUSES_ADMIN[choosenTab || "0"] === "COMPLETED" && (
              <div className={styles.buttonWrapper}>
                <Button
                  className={styles.button}
                  style={{ backgroundColor: "transparent", border: "2px solid #FFCC4A" }}
                  type="primary"
                  onClick={() => {
                    console.log("details");
                  }}
                >
                  Invoice
                </Button>
              </div>
            )}
            <div className={styles.buttonWrapper}>
              <Link to={{ pathname: `/dispute/${debate.id}` }}>
                <Button type="primary" className={styles.button}>
                  Details
                </Button>
              </Link>
            </div>
          </div>
        ),
      })),
    [choosenTab, debates?.debates],
  );
  return (
    <div className={styles.container}>
      <TableWithPagination
        paginationTable={{ pagination, skipAds: skipAds }}
        propsTable={{
          columns,
          dataSource: source,
          headerShow: true,
          isLoading: isDebateTableLoading,
          classList: styles.table,
        }}
      />
    </div>
  );
};
